import React, { useEffect, useRef, useState } from "react";
import { Pagination } from "./Pagination";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { User, Shield, RefreshCw, Search, ChevronUp, ChevronDown } from "lucide-react";

interface UserListProps {
    agent_id?: string;
    organization_id?: string;
    asset_id?: string;
    client_id?: string;
    platform?: string;
    os_name?: string;
}

const DEFAULT_PER_PAGE = 10;

const parseGroups = (groupsVal: any): string[] => {
    if (!groupsVal) return [];
    if (Array.isArray(groupsVal)) return groupsVal.filter(Boolean);
    if (typeof groupsVal === "string") {
        const trimmed = groupsVal.trim();
        if (!trimmed || trimmed === "[]") return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) return parsed.filter(Boolean);
        } catch {
            if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
                return trimmed.slice(1, -1).split(",").map(g => g.trim().replace(/^"|"$/g, '')).filter(Boolean);
            }
            return [trimmed];
        }
    }
    return [];
};

const GroupCell: React.FC<{ groups: any }> = ({ groups }) => {
    const [showAll, setShowAll] = useState(false);
    const parsedGroups = parseGroups(groups);

    if (parsedGroups.length === 0) return <span className="text-[10px] text-slate-400 dark:text-gray-400">-</span>;

    if (showAll || parsedGroups.length <= 2) {
        return (
            <div className="flex flex-wrap gap-1 items-center max-w-[320px]">
                {parsedGroups.map((group: string, gIdx: number) => (
                    <span
                        key={gIdx}
                        className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/20 text-[9px] font-semibold truncate max-w-[180px]"
                        title={group}
                    >
                        {group}
                    </span>
                ))}
                {parsedGroups.length > 2 && (
                    <button
                        type="button"
                        onClick={() => setShowAll(false)}
                        className="px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-200 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-gray-700 text-[9px] font-bold shrink-0 cursor-pointer transition-colors"
                        title="Show Less"
                    >
                        Show Less
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-wrap gap-1 items-center max-w-[240px]">
            {parsedGroups.slice(0, 2).map((group: string, gIdx: number) => (
                <span
                    key={gIdx}
                    className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/20 text-[9px] font-semibold truncate max-w-[150px]"
                    title={group}
                >
                    {group}
                </span>
            ))}
            <button
                type="button"
                onClick={() => setShowAll(true)}
                className="px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 hover:bg-slate-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-300 border border-slate-200 dark:border-gray-700 text-[9px] font-bold shrink-0 cursor-pointer transition-all"
                title="Click to view all groups"
            >
                +{parsedGroups.length - 2} more
            </button>
        </div>
    );
};

const UserList: React.FC<UserListProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
    platform,
    os_name,
}) => {
    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const platformStr = (platform || info?.platform || os_name || info?.os_name || "").toLowerCase();
    const isMac = platformStr.includes("mac") || platformStr.includes("darwin") || platformStr.includes("osx") || platform?.includes("macOS");

    const [users, setUsers] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>("");
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [searchInput, setSearchInput] = useState<string>("");
    const [loading, setLoading] = useState(true);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            const trimmed = value.trim();
            setSearch(trimmed);
            setPage(1);
            fetchUsers(0, perPage, trimmed, sortConfig); // ← add this
        }, 400);
    };

    const fetchUsers = async (
        skip: number = 0,
        limit: number = perPage,
        searchTerm: string = search,
        currentSort: { key: string; direction: "asc" | "desc" } | null = sortConfig
    ) => {
        setLoading(true);
        try {
            const queryObj: any = { client_id: client_id || info?.client_id, organization_id: organization_id || info?.organization_id, agent_id: agent_id || info?.agent_id, asset_id: asset_id || info?.asset_id };
            if (searchTerm) {
                queryObj.$or = [
                    { name: { $ilike: `%${searchTerm}%` } },
                    { full_name: { $ilike: `%${searchTerm}%` } },
                    { source: { $ilike: `%${searchTerm}%` } },
                    { description: { $ilike: `%${searchTerm}%` } },
                    { user_type: { $ilike: `%${searchTerm}%` } },
                    { status: { $ilike: `%${searchTerm}%` } },
                    { last_logon: { $ilike: `%${searchTerm}%` } },
                    { password_last_set: { $ilike: `%${searchTerm}%` } },
                ];
            }

            const params: any = {
                skip: skip,
                limit: limit,
                query: JSON.stringify(queryObj),
            };

            // Add API Sorting if requested
            if (currentSort) {
                params.sort_by = currentSort.key;
                params.sort_order = currentSort.direction;
            }

            const response = await baseapi.get("/asset_users", { params });
            setUsers(response.data?.data ?? []);
            setTotal(response.data?.total ?? 0);
        } catch (err: any) {
            showError(err.message);
            setUsers([]);
            setTotal(0);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchUsers(0, perPage, "", null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            if (debounceTimer.current) clearTimeout(debounceTimer.current); // ← add this
            const trimmedSearch = searchInput.trim();
            setSearch(trimmedSearch);
            setPage(1);
            fetchUsers(0, perPage, trimmedSearch, sortConfig);
        }
    };

    // const handleSearchButton = () => {
    //     const trimmedSearch = searchInput.trim();
    //     setSearch(trimmedSearch);
    //     setPage(1);
    //     fetchUsers(0, perPage, trimmedSearch, sortConfig);
    // };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= Math.ceil(total / perPage)) {
            setPage(newPage);
            fetchUsers((perPage * (newPage - 1)), perPage, search, sortConfig);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
        fetchUsers(0, value, search, sortConfig);
    };

    const handleRefresh = async () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const newSearch = "";
        setSearch(newSearch);
        setSearchInput("");
        fetchUsers(0, perPage, newSearch, sortConfig);
    };

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }

        const newSortConfig = { key, direction: newDirection };
        setSortConfig(newSortConfig);
        setPage(1); // Reset to page 1 on sort
        fetchUsers(0, perPage, search, newSortConfig);
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, widthClass }: { label: string; sortKey: string; widthClass?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`${widthClass} px-3 py-2 text-left text-[10px] font-bold text-slate-700 dark:text-gray-200 uppercase tracking-wider border-b border-slate-200 dark:border-gray-700 cursor-pointer group select-none hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-600 dark:text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-600 dark:text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-slate-400 dark:text-gray-400 group-hover:text-slate-600 dark:group-hover:text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    return (
        <div className="p-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <User className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">System Users</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {total || '...'} users found
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>

                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search by user..."
                            className="w-64 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-8 shadow-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white dark:bg-gray-900/40 rounded-md border border-slate-200 dark:border-gray-700 overflow-hidden shadow-sm">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left min-w-[1650px]">
                        <thead className="bg-slate-50 dark:bg-gray-800/80">
                            <tr>
                                <SortableHeader widthClass="min-w-[150px]" label="User" sortKey="name" />
                                <SortableHeader widthClass="min-w-[120px]" label="Full Name" sortKey="full_name" />
                                <SortableHeader widthClass="min-w-[80px]" label="Type" sortKey="user_type" />
                                <SortableHeader widthClass="min-w-[80px]" label="Status" sortKey="status" />
                                {!isMac && <SortableHeader widthClass="min-w-[150px]" label="Groups" sortKey="groups" />}
                                <SortableHeader widthClass="min-w-[80px]" label="Tag" sortKey="tag" />
                                <SortableHeader widthClass="min-w-[180px]" label="Description" sortKey="description" />
                                <SortableHeader widthClass="min-w-[100px]" label="Source" sortKey="source" />
                                <SortableHeader widthClass="min-w-[120px]" label="Last Logon" sortKey="last_logon" />
                                <SortableHeader widthClass="min-w-[130px]" label="Pwd Last Set" sortKey="password_last_set" />
                                <SortableHeader widthClass="min-w-[110px]" label="Pwd Required" sortKey="password_required" />
                                <SortableHeader widthClass="min-w-[120px]" label="Pwd Expires" sortKey="password_expires" />
                                <SortableHeader widthClass="min-w-[120px]" label="Acc Expires" sortKey="account_expires" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                            {loading ? (
                                <tr>
                                    <td colSpan={isMac ? 13 : 14} className="py-8 text-center text-slate-500 dark:text-gray-200 text-[11px]">Loading users...</td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={isMac ? 13 : 14} className="py-8 text-center text-slate-500 dark:text-gray-200 text-[11px]">No users found</td>
                                </tr>
                            ) : (
                                users.map((user, idx) => (
                                    <tr key={user.id ?? idx} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors duration-100">
                                        <td className="px-3 py-2">
                                            <div className="flex items-center text-[11px] font-semibold text-slate-900 dark:text-white truncate">
                                                <Shield
                                                    className={`w-3.5 h-3.5 mr-2 shrink-0 ${user.tag === "systemaccount"
                                                        ? "text-cyan-600 dark:text-cyan-400"
                                                        : user.tag === "serviceaccount"
                                                            ? "text-orange-600 dark:text-orange-400"
                                                            : "text-purple-600 dark:text-purple-400"
                                                        }`}
                                                />
                                                <span className="truncate" title={user.name}>{user.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-[11px] text-slate-600 dark:text-gray-300 truncate" title={user.full_name}>{user.full_name || "-"}</td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 truncate">{user.user_type || "-"}</td>
                                        <td className="px-3 py-2">
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${user.status === "Active"
                                                ? "text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/20 bg-green-50 dark:bg-green-500/10"
                                                : "text-slate-600 dark:text-gray-200 border-slate-200 dark:border-gray-500/20 bg-slate-50 dark:bg-gray-500/10"
                                                }`}>
                                                {user.status || "-"}
                                            </span>
                                        </td>
                                        {!isMac && (
                                            <td className="px-3 py-2">
                                                <GroupCell groups={user.groups} />
                                            </td>
                                        )}
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 truncate">
                                            {user.tag ? (
                                                <span className="px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/30 rounded text-[9px] font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">
                                                    {user.tag}
                                                </span>
                                            ) : "-"}
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 truncate" title={user.description}>{user.description || "-"}</td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 truncate">{user.source || "-"}</td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 font-mono">{user.last_logon || "-"}</td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 font-mono" title={user.password_last_set}>{user.password_last_set ? user.password_last_set.replace('T', ' ') : "-"}</td>
                                        <td className="px-3 py-2">
                                            {user.password_required !== null && user.password_required !== undefined ? (
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${user.password_required === true || user.password_required === "true"
                                                    ? "text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10"
                                                    : "text-slate-500 dark:text-gray-400 border-slate-200 dark:border-gray-500/20 bg-slate-50 dark:bg-gray-500/10"
                                                    }`}>
                                                    {user.password_required === true || user.password_required === "true" ? "Yes" : "No"}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-slate-400 dark:text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 font-mono">{user.password_expires || "-"}</td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-200 font-mono">{user.account_expires || "-"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={page}
                    totalPages={Math.max(1, Math.ceil(total / perPage))}
                    onPageChange={handlePageChange}
                    itemsPerPage={perPage}
                    totalItems={total}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            </div>
        </div>
    );
};

export default UserList;